import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combina clases de Tailwind CSS de forma segura
 * Combina clsx para clases condicionales y tailwind-merge para resolver conflictos
 * 
 * @example
 * cn('px-2 py-1', isActive && 'bg-primary-500', className)
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formatea un número con separadores de miles
 * 
 * @example
 * formatNumber(1234567) // "1,234,567"
 * formatNumber(1234.56, { decimals: 2 }) // "1,234.56"
 */
export function formatNumber(
  value: number | string | null | undefined,
  options?: {
    decimals?: number;
    locale?: string;
  }
): string {
  if (value === null || value === undefined) return '0';
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0';
  
  const { decimals, locale = 'es-AR' } = options || {};
  
  if (decimals !== undefined) {
    return num.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  
  return num.toLocaleString(locale);
}

/**
 * Formatea un número como porcentaje
 * 
 * @example
 * formatPercent(0.1234) // "12.34%"
 * formatPercent(0.1234, { decimals: 1 }) // "12.3%"
 */
export function formatPercent(
  value: number | string | null | undefined,
  options?: {
    decimals?: number;
  }
): string {
  if (value === null || value === undefined) return '0%';
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0%';
  
  const { decimals = 2 } = options || {};
  return `${(num * 100).toFixed(decimals)}%`;
}

/**
 * Trunca un string a una longitud máxima
 * 
 * @example
 * truncate('Hello world', 5) // "Hello..."
 */
export function truncate(str: string | null | undefined, maxLength: number): string {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return `${str.substring(0, maxLength)}...`;
}

/**
 * Trunca un string mostrando inicio y fin (para wallets, hashes, etc.)
 * 
 * @example
 * truncateMiddle('0x1234567890abcdef', 6, 4) // "0x1234...cdef"
 */
export function truncateMiddle(
  str: string | null | undefined, 
  startChars: number = 6, 
  endChars: number = 4
): string {
  if (!str) return '';
  if (str.length <= startChars + endChars + 3) return str;
  return `${str.substring(0, startChars)}...${str.substring(str.length - endChars)}`;
}

